/**
 * AVTR Mega Menu v2.0
 * AvatarDesigns Design System
 *
 * — Desktop: hover-triggered dropdown with 150ms debounce
 * — Scroll: transparent → white header state management
 * — Mobile:  left-side drawer with accordion sub-menus
 * — Overlay: page dimmer when menu is open
 * — Accessibility: keyboard nav + focus trap in drawer
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────
     SELECTORS & CLASSES
  ───────────────────────────────────────── */
  var SEL_NAV          = '.avtr-nav';
  var SEL_NAV_ITEM     = '.avtr-nav__item[data-mega]';
  var SEL_DROPDOWN     = '.avtr-mega-dropdown';
  var SEL_OVERLAY      = '.avtr-mega-overlay';
  var SEL_DRAWER       = '.avtr-drawer';
  var SEL_DRAWER_OV    = '.avtr-drawer-overlay';
  var SEL_HAMBURGER    = '.avtr-nav__hamburger';
  var SEL_DRAWER_CLOSE = '.avtr-drawer__close';
  var SEL_ACCORD_BTN   = '.avtr-drawer__primary-btn[data-accordion]';
  var SEL_ACCORD_ITEM  = '.avtr-drawer__primary-item';

  var CLS_NAV_OPEN     = 'avtr-nav--menu-open';
  var CLS_MENU_VIS     = 'is-visible';
  var CLS_ITEM_ACTIVE  = 'is-active';
  var CLS_DRAWER_OPEN  = 'is-open';
  var CLS_OV_OPEN      = 'is-open';
  var CLS_SCROLLED     = 'avtr-cinema-scrolled';
  var CLS_BODY_NO_SCR  = 'avtr-no-scroll';

  /* ─────────────────────────────────────────
     STATE
  ───────────────────────────────────────── */
  var activeDropdown   = null;  // Currently visible dropdown el
  var activeNavItem    = null;  // Currently hovered nav item
  var openTimer        = null;  // Debounce open timer
  var closeTimer       = null;  // Delayed close timer
  var OPEN_DELAY       = 150;   // ms — debounce before showing
  var CLOSE_DELAY      = 200;   // ms — grace period before hiding
  var isDrawerOpen     = false;

  /* ─────────────────────────────────────────
     ELEMENTS
  ───────────────────────────────────────── */
  var nav, navItems, overlay, drawer, drawerOverlay, hamburger;

  /* ─────────────────────────────────────────
     1. DESKTOP MEGA MENU
  ───────────────────────────────────────── */

  /**
   * Show the dropdown linked to a nav item.
   * Called after the debounce delay.
   */
  function showDropdown(item) {
    var menuId  = item.getAttribute('data-mega');
    var dropdown = document.getElementById(menuId);
    if (!dropdown) return;

    /* Hide any other open dropdown */
    if (activeDropdown && activeDropdown !== dropdown) {
      hideDropdownImmediate(activeDropdown, activeNavItem);
    }

    activeDropdown = dropdown;
    activeNavItem  = item;

    item.classList.add(CLS_ITEM_ACTIVE);
    dropdown.classList.add(CLS_MENU_VIS);

    /* Make nav solid white */
    if (nav) nav.classList.add(CLS_NAV_OPEN);

    /* Show page overlay */
    if (overlay) overlay.classList.add(CLS_OV_OPEN);
  }

  /**
   * Hide a specific dropdown — immediate, no delay.
   */
  function hideDropdownImmediate(dropdown, item) {
    if (!dropdown) return;
    dropdown.classList.remove(CLS_MENU_VIS);
    if (item) item.classList.remove(CLS_ITEM_ACTIVE);
    if (dropdown === activeDropdown) {
      activeDropdown = null;
      activeNavItem  = null;
    }
  }

  /**
   * Hide all open dropdowns and reset nav state.
   */
  function hideAll() {
    if (activeDropdown) {
      activeDropdown.classList.remove(CLS_MENU_VIS);
    }
    if (activeNavItem) {
      activeNavItem.classList.remove(CLS_ITEM_ACTIVE);
    }
    activeDropdown = null;
    activeNavItem  = null;

    if (nav) nav.classList.remove(CLS_NAV_OPEN);
    if (overlay) overlay.classList.remove(CLS_OV_OPEN);
  }

  /**
   * Attach mouseenter / mouseleave to each nav item with data-mega.
   */
  function initDesktopMenu() {
    navItems.forEach(function (item) {
      var menuId   = item.getAttribute('data-mega');
      var dropdown = document.getElementById(menuId);
      if (!dropdown) return;

      /* ── mouseenter on nav item ── */
      item.addEventListener('mouseenter', function () {
        clearTimeout(closeTimer);
        clearTimeout(openTimer);
        openTimer = setTimeout(function () {
          showDropdown(item);
        }, OPEN_DELAY);
      });

      /* ── mouseleave on nav item ── */
      item.addEventListener('mouseleave', function () {
        clearTimeout(openTimer);
        closeTimer = setTimeout(function () {
          /* Only hide if mouse isn't in the dropdown */
          if (!dropdown.matches(':hover')) {
            hideAll();
          }
        }, CLOSE_DELAY);
      });

      /* ── mouseenter on dropdown itself ── */
      dropdown.addEventListener('mouseenter', function () {
        clearTimeout(closeTimer);
      });

      /* ── mouseleave on dropdown ── */
      dropdown.addEventListener('mouseleave', function () {
        clearTimeout(openTimer);
        closeTimer = setTimeout(function () {
          /* Only hide if mouse isn't on the nav item */
          if (!item.matches(':hover')) {
            hideAll();
          }
        }, CLOSE_DELAY);
      });

      /* ── Click on link inside item (non-mega) ── */
      var link = item.querySelector('.avtr-nav__link[href]');
      if (link && !item.getAttribute('data-mega-trigger')) {
        link.addEventListener('click', hideAll);
      }
    });

    /* Hide on overlay click */
    if (overlay) {
      overlay.addEventListener('click', hideAll);
    }

    /* Hide on Escape */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        clearTimeout(openTimer);
        clearTimeout(closeTimer);
        hideAll();
      }
    });
  }

  /* ─────────────────────────────────────────
     2. MOBILE DRAWER
  ───────────────────────────────────────── */

  function openDrawer() {
    console.log("AVTR Mega Menu: openDrawer() called. drawer:", drawer);
    if (!drawer) return;
    isDrawerOpen = true;
    drawer.classList.add(CLS_DRAWER_OPEN);
    if (drawerOverlay) drawerOverlay.classList.add(CLS_OV_OPEN);
    document.body.classList.add(CLS_BODY_NO_SCR);
    if (hamburger) hamburger.setAttribute('aria-expanded', 'true');

    /* Focus first link for a11y */
    var firstBtn = drawer.querySelector('.avtr-drawer__primary-btn');
    if (firstBtn) {
      setTimeout(function () { firstBtn.focus(); }, 50);
    }
  }

  function closeDrawer() {
    console.log("AVTR Mega Menu: closeDrawer() called. drawer:", drawer);
    if (!drawer) return;
    isDrawerOpen = false;
    drawer.classList.remove(CLS_DRAWER_OPEN);
    if (drawerOverlay) drawerOverlay.classList.remove(CLS_OV_OPEN);
    document.body.classList.remove(CLS_BODY_NO_SCR);
    if (hamburger) hamburger.setAttribute('aria-expanded', 'false');

    /* Reset all subpanels to hidden */
    var subpanels = drawer.querySelectorAll('.avtr-drawer__subpanel');
    subpanels.forEach(function (panel) {
      panel.classList.remove('is-active');
      panel.setAttribute('aria-hidden', 'true');
    });

    /* Reset all primary triggers expanded state */
    var triggers = drawer.querySelectorAll('.avtr-drawer__primary-btn[data-subpanel]');
    triggers.forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
  }

  function initDrawer() {
    if (!drawer) return;

    /* Hamburger open */
    if (hamburger) {
      hamburger.addEventListener('click', function () {
        console.log("AVTR Mega Menu: Hamburger clicked! isDrawerOpen:", isDrawerOpen);
        isDrawerOpen ? closeDrawer() : openDrawer();
      });
    }

    /* Close button */
    var closeBtn = drawer.querySelector(SEL_DRAWER_CLOSE);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

    /* Backdrop click */
    if (drawerOverlay) drawerOverlay.addEventListener('click', closeDrawer);

    /* Escape key */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isDrawerOpen) closeDrawer();
    });

    /* Subpanel sliding navigation (drill-down layers) */
    var subpanelTriggers = drawer.querySelectorAll('.avtr-drawer__primary-btn[data-subpanel]');
    subpanelTriggers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var panelId = btn.getAttribute('data-subpanel');
        var panel = drawer.querySelector('#' + panelId);
        if (panel) {
          panel.classList.add('is-active');
          panel.setAttribute('aria-hidden', 'false');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });

    var subpanelBackBtns = drawer.querySelectorAll('[data-close-subpanel]');
    subpanelBackBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var panel = btn.closest('.avtr-drawer__subpanel');
        if (panel) {
          panel.classList.remove('is-active');
          panel.setAttribute('aria-hidden', 'true');

          // Reset corresponding trigger expanded state
          var trigger = drawer.querySelector('[data-subpanel="' + panel.id + '"]');
          if (trigger) {
            trigger.setAttribute('aria-expanded', 'false');
          }
        }
      });
    });

    /* Tab switching (SHOP / INFO) */
    var tabs = drawer.querySelectorAll('.avtr-drawer__tab');
    var panels = drawer.querySelectorAll('.avtr-drawer__tab-panel');
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var targetPanel = tab.getAttribute('data-tab');

        /* Deactivate all tabs and panels */
        tabs.forEach(function (t) {
          t.classList.remove('is-active');
          t.setAttribute('aria-selected', 'false');
        });
        panels.forEach(function (p) {
          p.classList.remove('is-active');
        });

        /* Activate clicked tab and its panel */
        tab.classList.add('is-active');
        tab.setAttribute('aria-selected', 'true');
        var panel = drawer.querySelector('[data-panel="' + targetPanel + '"]');
        if (panel) panel.classList.add('is-active');
      });
    });
  }

  /* ─────────────────────────────────────────
     3. SCROLL STATE (transparent → white)
     Works in tandem with avtr-header-cinema.js
     on non-cinema pages (product, collection…)
  ───────────────────────────────────────── */

  var scrollTicking = false;
  var SCROLL_THRESHOLD = 10;
  var lastScrollTop = 0;
  var scrollHideThreshold = 150;

  function updateNavOnScroll() {
    var scrollY = window.pageYOffset || document.documentElement.scrollTop;
    if (!nav) { scrollTicking = false; return; }

    /* Only manage data-scrolled (transparent vs solid background) on non-cinema pages */
    if (!document.body.classList.contains('avtr-cinema-ready')) {
      if (scrollY > SCROLL_THRESHOLD) {
        nav.setAttribute('data-scrolled', 'true');
      } else {
        nav.removeAttribute('data-scrolled');
      }
    }

    /* Scroll direction logic (hide / show) for all pages */
    var scrollYClamped = scrollY < 0 ? 0 : scrollY; // Prevent negative scroll values (bounce on iOS)

    if (scrollYClamped > scrollHideThreshold) {
      if (scrollYClamped > lastScrollTop) {
        // Scrolling down -> hide
        nav.classList.add('avtr-nav--hidden');
      } else {
        // Scrolling up -> show
        nav.classList.remove('avtr-nav--hidden');
      }
    } else {
      // Near top -> always show
      nav.classList.remove('avtr-nav--hidden');
    }

    lastScrollTop = scrollYClamped;
    scrollTicking = false;
  }

  function initScrollWatch() {
    window.addEventListener('scroll', function () {
      if (!scrollTicking) {
        requestAnimationFrame(updateNavOnScroll);
        scrollTicking = true;
      }
    }, { passive: true });
    updateNavOnScroll();
  }

  /* ─────────────────────────────────────────
     4. CSS CUSTOM PROPERTY SYNC
     Sets --avtr-announce-h so the mega dropdown
     positions correctly below the announcement bar
  ───────────────────────────────────────── */

  function syncAnnounceHeight() {
    var announceBar = document.querySelector('.announcement-bar-section');
    var h = announceBar ? announceBar.offsetHeight : 0;
    document.documentElement.style.setProperty('--avtr-announce-h', h + 'px');
  }

  /* ─────────────────────────────────────────
     5. RESIZE HANDLER
  ───────────────────────────────────────── */

  var resizeTimer = null;
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      syncAnnounceHeight();
      /* Close drawer on desktop resize */
      if (window.innerWidth >= 1024 && isDrawerOpen) {
        closeDrawer();
      }
      /* Hide any open dropdown */
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      hideAll();
    }, 150);
  }

  /* ─────────────────────────────────────────
     6. INIT
  ───────────────────────────────────────── */

  function init() {
    nav          = document.querySelector(SEL_NAV);
    navItems     = Array.prototype.slice.call(document.querySelectorAll(SEL_NAV_ITEM));
    overlay      = document.querySelector(SEL_OVERLAY);
    drawer       = document.querySelector(SEL_DRAWER);
    drawerOverlay = document.querySelector(SEL_DRAWER_OV);
    hamburger    = document.querySelector(SEL_HAMBURGER);

    console.log("AVTR Mega Menu init: nav=", nav, "drawer=", drawer, "hamburger=", hamburger, "drawerOverlay=", drawerOverlay);

    if (!nav) {
      console.warn("AVTR Mega Menu init failed: SEL_NAV (.avtr-nav) element not found in DOM.");
      return;
    }

    syncAnnounceHeight();

    if (navItems.length) initDesktopMenu();
    initDrawer();
    initScrollWatch();

    window.addEventListener('resize', onResize, { passive: true });
  }

  /* ─────────────────────────────────────────
     7. SHOPIFY DESIGN MODE SUPPORT
  ───────────────────────────────────────── */

  document.addEventListener('shopify:section:load', function () {
    syncAnnounceHeight();
  });

  /* ─────────────────────────────────────────
     BOOTSTRAP
  ───────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

/* 2026-10-18: mantener --avtr-announce-h igual al alto real de la barra (al cargar la fuente la barra se achica y el header quedaba con una rayita blanca encima) */
(function () {
  function sync() {
    var el = document.querySelector('.announcement-bar-section');
    if (!el || typeof ResizeObserver === "undefined") return;
    var ro = new ResizeObserver(function () {
      if ((el.style.transform || '').indexOf('-100%') !== -1) return;
      document.documentElement.style.setProperty('--avtr-announce-h', el.offsetHeight + 'px');
    });
    ro.observe(el);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync); else sync();
})();

/* 2026-10-18: barra de anuncios circular (la flecha < en el primer mensaje no hacía nada) */
(function () {
  function init() {
    var sec = document.querySelector('.announcement-bar-section');
    if (!sec) return;
    var slider = sec.querySelector('.slider, .announcement-bar__slider');
    if (!slider) return;
    sec.addEventListener('click', function (e) {
      var btn = e.target.closest('button[name="previous"], button[name="next"]');
      if (!btn) return;
      var max = slider.scrollWidth - slider.clientWidth;
      if (btn.name === 'previous' && slider.scrollLeft <= 2) {
        e.preventDefault(); e.stopImmediatePropagation();
        slider.scrollTo({ left: max, behavior: 'smooth' });
      } else if (btn.name === 'next' && slider.scrollLeft >= max - 2) {
        e.preventDefault(); e.stopImmediatePropagation();
        slider.scrollTo({ left: 0, behavior: 'smooth' });
      }
    }, true);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();

/* 2026-10-19: botón "Comprar ahora": agrega la talla elegida y va directo al pago */
(function () {
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-avtr-buy-now]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var form = btn.closest('form');
    if (!form) return;
    var id = form.querySelector('input[name="id"]');
    var qty = document.querySelector('input[name="quantity"][form="' + form.id + '"]') || form.querySelector('input[name="quantity"]');
    var q = qty ? parseInt(qty.value, 10) || 1 : 1;
    if (!id || !id.value) return;
    btn.setAttribute('aria-busy', 'true'); btn.style.opacity = '.6';
    window.location.href = '/cart/' + id.value + ':' + q + '?checkout';
  });
})();

/* 2026-10-19: Black Week en la barra de anuncios (fecha de Colombia, se activa sola)
   hasta 2 nov: regalo · 19-22 nov: cuenta regresiva · 23-30 nov: descuentos sin regalo · 30 nov: último día · desde 1 dic: mensajes normales */
(function () {
  function bogotaYMD() {
    try {
      var p = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Bogota', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
      return parseInt(p.replace(/-/g, ''), 10);
    } catch (e) { return 0; }
  }
  function apply() {
    var d = bogotaYMD();
    var msgs = null;
    var gift = window.innerWidth < 750 ? 'JEAN + HOODIE: ELIGE TU REGALO' : 'JEAN + HOODIE, 2 HOODIES O 2 JEANS DE MUJER: ELIGE TU REGALO';
    if (d >= 20261019 && d <= 20261102) {
      /* 2026-10-26: Steven cortó el regalo al 2-nov */
      msgs = [d === 20261102 ? 'ÚLTIMO DÍA PARA ELEGIR TU REGALO' : gift, 'ENVÍOS GRATIS DESDE $300.000', 'ENTREGA EN 1-3 DÍAS HÁBILES'];
    } else if (d >= 20261119 && d <= 20261122) {
      var left = 20261123 - d;
      var c = left === 1 ? 'BLACK WEEK EMPIEZA MAÑANA' : 'BLACK WEEK EN ' + left + ' DÍAS';
      msgs = [c, 'ENVÍOS GRATIS DESDE $300.000', 'ENTREGA EN 1-3 DÍAS HÁBILES'];
    } else if (d >= 20261123 && d <= 20261130) {
      msgs = [
        'BLACK WEEK · 20% EN JEANS, HOODIES, CAMISETAS Y TOPS',
        '10% EN GORRAS · HASTA EL 30 DE NOVIEMBRE',
        d === 20261130 ? 'ÚLTIMO DÍA DE BLACK WEEK' : 'ENVÍOS GRATIS DESDE $300.000'
      ];
    }
    if (!msgs) return;
    var spans = document.querySelectorAll('.announcement-bar__message span');
    var i = 0;
    spans.forEach(function (s) { s.textContent = msgs[i % msgs.length]; i++; });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', apply); else apply();
})();
